import {useConfig} from '@aboutyou/config';

import {FreeDeliverySection} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/basket_mov.types';
import {ProductTileV2} from '@aboutyou/grpc-clients/tadarida/aysa_api/models/product_tile_v2.types';
import {AddItemResponseStatus_Success_Section} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/basket/v1/add_item_status.types';
import {useTranslation} from '@aboutyou/i18n';
import {
  useTrackFeature,
  useTrackFeatureInViewImpression,
} from '@aboutyou/tracking';
import {Accordion, ProductImage, Text} from '@aboutyou/ui-components';
import React from 'react';
import styled from 'styled-components';
import {useMiniAdpDialogOpenedState} from '../../../../features/MiniADPDialog/useMiniAdpDialogOpenedState';
import {ProductMiniAdp} from '../../../../features/ProductMiniAdp/ProductMiniAdp';
import {
  ProductTile,
  ProductTileProps,
} from '../../../../features/ProductTile/ProductTile';
import {ProductList} from '../../../../features/Recommendations/ProductList';
import {SizeSelectionOpenedStateKey} from '../../../../features/SizeSelectionFlyout/hooks/useSizeSelectionOpenedState';
import {RecommendationsComponentProps} from '../BasketMOVView';

const ProductTileWithMovAddLabel: React.FC<ProductTileProps> = props => {
  const {t} = useTranslation();
  return <ProductTile {...props} addCtaLabel={t('Add')} />;
};

export const BasketMOVAccordion: React.FC<RecommendationsComponentProps> = ({
  movUpsellingRecommendationSection,
  onAddToBasketSuccess,
  refetchBasketSection,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);

  const {
    isOpen: isFlyoutOpen,
    close: closeFlyout,
    open: openFlyout,
    productId,
    selectedSize,
    setSelectedSize,
  } = useMiniAdpDialogOpenedState();

  const {device} = useConfig();
  const trackFeature = useTrackFeature();

  const {ref} = useTrackFeatureInViewImpression<HTMLDivElement>({
    label: 'mov_reco',
    name: 'mov_reco',
  });

  const onA2BSuccessHandler = async ({
    freeDeliverySection,
    statusSuccessSections,
  }: {
    freeDeliverySection?: FreeDeliverySection;
    statusSuccessSections: AddItemResponseStatus_Success_Section[];
  }) => {
    await refetchBasketSection();
    onAddToBasketSuccess?.({freeDeliverySection, statusSuccessSections});
  };

  const handleOpen = () => {
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const movRecoProducts = movUpsellingRecommendationSection.products;

  const trackOpen = () => {
    trackFeature({eventType: 'click', label: 'mov_info'});
  };

  if (movRecoProducts.length === 0) {
    return null;
  }

  return (
    <>
      <Wrapper data-testid="basketMovRecommendation">
        <StyledAccordion
          content={
            <HeaderWrapper>
              <TextWrapper>
                <Text>{movUpsellingRecommendationSection.headline}</Text>
              </TextWrapper>
              <ProductThumbnails show={!isOpen} products={movRecoProducts} />
            </HeaderWrapper>
          }
          toggleContent={
            <div ref={ref}>
              <ProductList
                uniqueKey="basket_mov_accordion"
                streamPosition={0}
                type="list"
                products={movRecoProducts}
                ProductTileComponent={ProductTileWithMovAddLabel}
                productTileConfig={{gridColumns: device === 'desktop' ? 4 : 2}}
                onAddToBasketCtaClick={({productId}) => openFlyout({productId})}
                promotionTracker={{
                  promotionId:
                    movUpsellingRecommendationSection.promotionTracking.name,
                  promotionName:
                    movUpsellingRecommendationSection.promotionTracking.name,
                  creativeName:
                    movUpsellingRecommendationSection.promotionTracking.name,
                  creativeSlot: '0',
                  locationId: 'mov_reco',
                }}
                trackComponentConfig={{
                  location_id: 'mov_reco',
                  page_type_id: 'basket',
                }}
              />
            </div>
          }
          onOpen={handleOpen}
          onClose={handleClose}
          trackOpen={trackOpen}
        />
      </Wrapper>
      <ProductMiniAdp
        close={closeFlyout}
        isOpen={isFlyoutOpen}
        productId={productId}
        selectedSize={selectedSize}
        setSelectedSize={setSelectedSize}
        onSuccess={onA2BSuccessHandler}
        sizeSelectionStateManagerKey={
          SizeSelectionOpenedStateKey.MAIN_SIZE_SELECTION
        }
        showToastMessage
        trackingData={{
          sectionIndex: 0,
          recoType: movUpsellingRecommendationSection.promotionTracking.name,
        }}
      />
    </>
  );
};

const ProductThumbnails = (props: {
  products: ProductTileV2[];
  show: boolean;
}) => {
  const {products, show} = props;

  const thumbnails = products.slice(0, 4).map(product => {
    const firstImage = product.gallery.elements[0];
    if (firstImage?.type?.$case === 'image') {
      return firstImage.type.image.image;
    }
    return {src: '', description: '', isEnclosed: false};
  });

  return (
    <ImagesWrapper data-show={show}>
      {thumbnails.map(thumbnail => (
        <ImageWrapper key={thumbnail.src}>
          <ProductImage
            src={thumbnail.src}
            alt={thumbnail.description}
            padded={thumbnail.isEnclosed}
            increasePadding
          />
        </ImageWrapper>
      ))}
    </ImagesWrapper>
  );
};

const ImagesWrapper = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-direction: row;
  flex-shrink: 1;
  margin-right: 0.75rem;
  opacity: 0;
  &[data-show='true'] {
    opacity: 1;
  }
  transition: opacity 0.3s ease-in-out;
`;
const ImageWrapper = styled.div`
  width: 35px;
`;

const Wrapper = styled.div`
  margin-top: 1rem;
`;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const TextWrapper = styled.div`
  flex: 1;
`;

const StyledAccordion = styled(Accordion)`
  padding: 0.5rem 1rem;
`;
